import { ChangeDetectionStrategy, Component, Input, OnInit, Self } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormControl,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { ENTER } from '@angular/cdk/keycodes';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatChipsModule } from '@angular/material/chips';
import { TranslateModule } from '@ngx-translate/core';
import { IconNames } from '../../common/constants/icon-names';
import { markRequiredControlWithAsteriskSymbol, parseChipsInputBulkDataEntry, removeInputElement } from '../../common';
import { FieldErrorMessages } from '../field-error-messages/field-error-messages.component';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { DirectivesModule } from '../../common/directives/directives.module';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'ui-chip-field',
  imports: [
    CommonModule,
    TranslateModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    FieldErrorMessages,
    MatChipsModule,
    SvgIconComponent,
    DirectivesModule,
  ],
  templateUrl: './chip-field.component.html',
  styleUrls: ['./chip-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChipFieldComponent implements OnInit, ControlValueAccessor {
  @Input() label: string;
  @Input() inputPlaceholder: string;
  @Input() hasErrorMessageContainer = false;
  @Input() isChipRemovable = true;
  @Input() isChipInputAddOnBlur = true;
  @Input() isDisabled = false;
  @Input() isInputReadonly = false;
  @Input() testId: string;
  @Input() removeDuplicates: boolean;
  @Input() hintText?: string;
  @Input() maxInputLength?: number;

  @Input() set submitted(submitted: boolean) {
    if (submitted) {
      this.chipInputFormControl.markAsTouched();
    }
  }

  @Input() shouldSplitWithSpace?: boolean = true;

  isFieldRequired: (control: AbstractControl) => string = markRequiredControlWithAsteriskSymbol;

  separatorKeysCodes: number[] = [ENTER];
  chipInputFormControl: UntypedFormControl = new UntypedFormControl('');
  disabled = false;
  iconNames = IconNames;
  value$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

  constructor(@Self() public ngControl: NgControl) {
    ngControl.valueAccessor = this;
  }

  ngOnInit(): void {
    if (this.isDisabled) {
      this.setDisabledState(this.isDisabled);
    }
  }

  registerOnChange(fn: () => string[]): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(value: string[]): void {
    this.value$.next(value);
  }

  onChange: (value: string[]) => string[];

  onTouched: () => void;

  setDisabledState(value: boolean): void {
    this.disabled = value;
    value ? this.chipInputFormControl.disable() : this.chipInputFormControl.enable();
  }

  addChip(
    control: UntypedFormControl | AbstractControl,
    input: HTMLInputElement,
    inputControl: UntypedFormControl,
  ): void {
    parseChipsInputBulkDataEntry(control, input, inputControl, null, this.shouldSplitWithSpace, this.removeDuplicates);
  }

  removeChip(control: UntypedFormControl | AbstractControl, element: string): void {
    removeInputElement(control, element);
  }
}
